// AgentsHub — Phase 3.5 consolidated Agents page.
//
// Mirror of PlayersHub for the sub-agent (downline) side. The bottom-
// nav "Agents" tab lands here directly; the page owns its own header
// (emerald-tinted, matching the bottom-nav section color) and two
// magenta sub-tabs:
//
//   /people/agents                → redirect → /people/agents/requests
//   /people/agents/requests       → AgentRequestsPanel (QR + queues)
//   /people/agents/list           → SubagentsListPanel (sub-agent roster)
//
// Detail/workflow routes (sub-agent detail) remain at sibling paths
// and are full pages without these sub-tabs:
//   /people/agents/:id
//
// Wallet sync: refreshAgentWallet() fires on every navigation into
// /people/agents/* so the chrome chip is always fresh before the
// agent approves a recharge / marks a withdrawal paid (each of which
// moves money in/out of their pool).

import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { refreshAgentWallet } from '@baaligames/shared-ui/admin/lib/agentWallet';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import MagentaTabBar from '@baaligames/shared-ui/admin/components/MagentaTabBar';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import { useDownlineLabel } from '@baaligames/shared-ui/admin/lib/downlineLabel';

// Phase R7 follow-up (2026-05-13): poll cadence for the pending
// agent-request counts. Mirrors PlayersHub.COUNT_POLL_MS.
// 2026-05-20 — bumped 30_000 → 60_000 to match the new bottom-nav
// rhythm (AgentBottomNav PENDING_POLL_MS now 60s).
const COUNT_POLL_MS = 60_000;

export default function AgentsHub() {
  const t = useT();
  const dl = useDownlineLabel();   // "Master(s)" for a Super Master, "Agent(s)" for a Master
  const loc = useLocation();
  useEffect(() => { refreshAgentWallet(); }, [loc.pathname]);

  // Phase R7 follow-up: live count of pending sub-agent recharge +
  // withdrawal requests, summed for the "Agent requests" top tab.
  const [pending, setPending] = useState<{ recharge: number; withdrawal: number }>({ recharge: 0, withdrawal: 0 });
  useEffect(() => {
    let alive = true;
    const BASE = '/admin/me-agent/payment-requests';
    const fetchCounts = async () => {
      try {
        const [r, w] = await Promise.all([
          adminApi.get<{ total: number }>(`${BASE}/agent-recharge?state=pending&limit=1&offset=0`),
          adminApi.get<{ total: number }>(`${BASE}/agent-withdrawal?state=pending&limit=1&offset=0`),
        ]);
        if (alive) setPending({ recharge: r.total || 0, withdrawal: w.total || 0 });
      } catch { /* keep last good value */ }
    };
    fetchCounts();
    // 2026-05-20 — skip when tab is hidden.
    const iv = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchCounts();
    }, COUNT_POLL_MS);
    return () => { alive = false; clearInterval(iv); };
  }, [loc.pathname]);
  const totalPending = pending.recharge + pending.withdrawal;

  return (
    <div className="max-w-xl lg:max-w-3xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4">
      {/* Header — emerald to match the bottom-nav section color. */}
      <motion.header
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        className="px-2 lg:px-4"
      >
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl
                           bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100
                           dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30">
            <Icon name="support_agent" size={22} filled />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-display font-semibold text-arena-900 dark:text-white tracking-tight">
              {dl.plural}
            </h1>
            <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70 truncate">
              Manage {dl.lowerPlural} and their requests
            </p>
          </div>
        </div>
      </motion.header>

      {/* Top sub-tabs */}
      <div className="px-2 lg:px-4">
        <MagentaTabBar
          size="lg"
          accent="emerald"
          items={[
            { to: '/people/agents/requests', label: 'Payments',        icon: 'inbox',         matchPrefix: '/people/agents/requests', count: totalPending },
            { to: '/people/agents/list',     label: `My ${dl.plural}`, icon: 'support_agent', matchPrefix: '/people/agents/list' },
          ]}
        />
      </div>

      {/* Active panel */}
      <div className="px-2 lg:px-4">
        <Outlet />
      </div>
    </div>
  );
}
